import { useNavigate } from "react-router-dom";

const HomePage = () => {
  const navigate = useNavigate();

  // NOTE: The '20+' users stat below should be verified for accuracy.
  // It seems low and might be a placeholder. Ensure it reflects truthful data.
  const stats = [
    { number: "20+", label: "Users Improving Skills" }, // Updated to 20+
    { number: "92%", label: "Interview Success Rate" },
    { number: "85%", label: "English Fluency Improvement" },
    { number: "15min", label: "Average Practice Session" },
  ];

  const handleStartPractice = () => {
    navigate("/interview");
  };

  const handleTestEnglish = () => {
    navigate("/english-practice");
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Hero Section */}
      <section aria-labelledby="hero-heading" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <div className="text-center">
            <h1 id="hero-heading" className="text-5xl lg:text-7xl font-bold text-white mb-4 leading-tight">
              Welcome to Practap
            </h1>
            <h2 className="text-4xl lg:text-5xl font-semibold text-gray-200 mb-8 leading-tight">
              Master Your Communication. Ace Your Interviews.
            </h2>

            <p className="text-xl text-slate-200 max-w-3xl mx-auto mb-10 leading-relaxed">
              Practap is your AI-powered practice space for clear, confident English communication—for everyone. Whether you’re preparing for a job interview, improving your public speaking, or just want to express yourself better, Practap gives you the tools to practice, get feedback, and grow.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
              <button
                onClick={handleStartPractice}
                className="group px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center"
              >
                Start Interview Practice
                <svg
                  aria-hidden="true"
                  className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </button>
              <button
                onClick={handleTestEnglish}
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-xl border border-white/20 hover:bg-white/20 transform hover:scale-105 transition-all duration-300"
              >
                Test English Skills
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 max-w-4xl mx-auto">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl lg:text-4xl font-bold text-white mb-2">
                    {stat.number}
                  </div>
                  <div className="text-slate-300 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Practice English with Confidence Section */}
      <section aria-labelledby="practice-english-heading" className="py-20 bg-slate-800/60"> {/* Slightly different background for variation */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="practice-english-heading" className="text-4xl font-bold text-white mb-4">
              🗣️ Practice English with Confidence
            </h2>
            {/* Optional: A brief paragraph below the title if needed, or straight to features */}
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                // icon: "🗣️", // Icon can be more elaborate if available
                title: "Real-time AI Feedback",
                description: "On your spoken and written English to help you improve faster.",
              },
              {
                // icon: "🎭",
                title: "Scenario Role-Play",
                description: "Practice common communication situations for work, study, or daily life.",
              },
              {
                // icon: "📈",
                title: "Fluency Development",
                description: "Build your ability to express yourself clearly and effectively.",
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 hover:border-purple-500/50 transition-all duration-300 transform hover:scale-105"
              >
                {/* Optional Icon Area */}
                {/* <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center text-white mb-6 text-2xl">
                  {feature.icon}
                </div> */}
                <h3 className="text-xl font-semibold text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Preparing for a QA Interview? Section */}
      <section aria-labelledby="qa-interview-heading" className="py-20 bg-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="qa-interview-heading" className="text-4xl font-bold text-white mb-4">
              🎯 Preparing for a QA Interview?
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              We’ve got you. Practap offers focused Quality Assurance interview preparation—designed by QA professionals.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {[
              {
                title: "Practice QA Questions",
                description: "Covering both Manual & Automation topics.",
                icon: "❓", // Placeholder
              },
              {
                title: "Expert Tips & Feedback",
                description: "On your responses to refine your answers.",
                icon: "💡", // Placeholder
              },
              {
                title: "Dual Skill Enhancement",
                description: "Improve both your technical interviewing and English communication abilities.",
                icon: "🛠️", // Placeholder
              },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 hover:border-green-500/50 transition-all duration-300 transform hover:scale-105"
              >
                {/* Optional Icon Area */}
                {/* <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-teal-600 rounded-xl flex items-center justify-center text-white mb-6 text-2xl">
                  {item.icon}
                </div> */}
                <h3 className="text-xl font-semibold text-white mb-3">
                  {item.title}
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center text-slate-300 space-y-3">
            <p className="text-lg">✅ English practice is open to everyone.</p>
            <p className="text-lg">🎓 QA interview prep is available for users preparing for QA-specific roles.</p>
          </div>
        </div>
      </section>

      {/* Why Practap? Section */}
      <section aria-labelledby="why-practap-heading" className="py-20 bg-slate-800/60"> {/* Consistent background with other new sections */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="why-practap-heading" className="text-4xl font-bold text-white mb-4">
              🔐 Why Practap?
            </h2>
            {/* Optional: A brief paragraph below the title if needed */}
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Accessible Anywhere",
                description: "Practice on your schedule, from any device, anytime.",
                // icon: "🌍",
              },
              {
                title: "Pressure-Free Practice",
                description: "Learn and improve at your own pace in a supportive environment.",
                // icon: "😌",
              },
              {
                title: "Holistic Skill Boost",
                description: "Enhance both your communication soft skills and technical readiness.",
                // icon: "🚀",
              },
            ].map((reason, index) => (
              <div
                key={index}
                className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 hover:border-indigo-500/50 transition-all duration-300 transform hover:scale-105"
              >
                {/* <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-sky-600 rounded-xl flex items-center justify-center text-white mb-6 text-2xl">
                  {reason.icon}
                </div> */}
                <h3 className="text-xl font-semibold text-white mb-3">
                  {reason.title}
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  {reason.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section aria-labelledby="cta-heading" className="py-20">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-slate-800 via-slate-800/80 to-slate-800 backdrop-blur-sm rounded-3xl p-12 border border-blue-500/20"> {/* Adjusted background for new CTA feel */}
            <h2 id="cta-heading" className="text-4xl font-bold text-white mb-6">
              🚀 Get Started
            </h2>
            <p className="text-xl text-slate-200 mb-10 leading-relaxed">
              Join Practap now and start practicing smarter. <br />
              Let your voice—and your confidence—shine.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <button
                onClick={() => navigate("/register")}
                className="group px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center"
              >
                👉 Sign Up
              </button>
              <button
                onClick={() => navigate("/pricing")} // Assuming /pricing for "Learn More" as per plan
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-xl border border-white/20 hover:bg-white/20 transform hover:scale-105 transition-all duration-300"
              >
                Learn More
              </button>
            </div>
            {/* Removed "No credit card required" text to simplify for new CTA */}
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
